import { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import {
  useTheme, Box, Grid, Card, Button, Divider, Typography,
  CardContent, useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import Title from '../../components/Title';
import { Office } from '../../models/office';
import { Collect } from '../../models/collect';
import { reportLend } from '../../models/reportlend';
import { formatCurrency } from '../../utils/format/currency';
import moment from 'moment-timezone';
import { nowOperationalDateString } from '../../utils/operationalDateTime';
import CollectFilter from '../../components/filter/collectbyDate';
import HeaderResponsive from '../../components/layouts/HeaderResponsive';
import EmptyStateMessage from '../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../components/table/SimpleCustomTable';
import { SimpleColumn, SimpleRow } from '../../components/table/interface';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface RefinanceRow extends SimpleRow {
  client: string;
  collect_id?: string;
  collect?: string;
  date?: string;
  value_start: number;
  balance: number;
  days_late: number | string;
  date_refinance: string;
  time_refinance: string;
  total_value: number;
  quote: number | string;
}

const RefinanceLend: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const current_date = nowOperationalDateString();

  const [office_id, setOffice_id] = useState('');
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [datas, setDatas] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const totalRowId = 'total-row';
  const totalCellStyles = { fontWeight: 600 } as const;

  useEffect(() => {
    axios.get('offices').then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    const fetchCollects = async () => {
      if (!office_id) return;

      showLoading();
      try {
        const response = await axios.get(`collectsoffice/${office_id}`);
        setCollectsOffice(response.data);
        setSelectedCollects([]);
        setSelectAll(false);

        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!response.data || response.data.length === 0) {
          showAlert('No existen registros para los datos seleccionados', 'info');
        }
      } catch (error) {
        console.error('Error fetching data', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchCollects();
  }, [office_id]);


  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedCollects(prev =>
      prev.includes(value) ? prev.filter(id => id !== value) : [...prev, value]
    );
  };

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = collectsOffice.map(collect => collect.id.toString());
      setSelectedCollects(allIds);
      setSelectAll(true);
    } else {
      setSelectedCollects([]);
      setSelectAll(false);
    }
  };

  const handleOpenModalLendType = (row: RefinanceRow) => {
    showAlert(`Detalle no disponible para ${row.client}`, 'info');
  };
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert('Debe seleccionar almenos 1 cobro', 'warning');
      return;
    }
    const formData = { end_date, start_date, collect_id: selectedCollects };
    showLoading();
    try {
      const response = await axios.post('/report/refinancebydate', formData);
      setDatas(response.data.data);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!response.data.data || response.data.data.length === 0) {
        showAlert('No existen registros para los datos seleccionados', 'info');
        return;
      }

    } catch (error) {
      console.error('Error:', error);
      // //console.log('Error fetching data:', 'error');
    } finally {
      hideLoading();
    }
  };

  const renderDataGridPorCobro = (groupData: reportLend[]) => {
    if (!groupData || groupData.length === 0) return null;

    const mappedRows: RefinanceRow[] = groupData.map((r) => {
      const dateFormatted = moment(r.date_refinance).format('YYYY-MM-DD');
      const timeFormatted = moment(r.date_refinance).format('HH:mm:ss');

      return {
        id: r.id,
        client: r.client.fullname,
        collect_id: String(r.collect.id),
        collect: r.collect.name,
        date: dateFormatted,
        date_refinance: dateFormatted,
        time_refinance: timeFormatted,
        value_start: Number(r.value_start ?? 0),
        balance: Number(r.balance ?? 0),
        days_late: r.days_late ?? 0,
        total_value: Number(r.total_value ?? 0),
        quote: r.count_pay ?? 0,
      };
    });

    const totalValueStart = mappedRows.reduce((acc, row) => acc + Number(row.value_start || 0), 0);
    const totalBalance = mappedRows.reduce((acc, row) => acc + Number(row.balance || 0), 0);
    const totalRefinanced = mappedRows.reduce((acc, row) => acc + Number(row.total_value || 0), 0);

    const rows: RefinanceRow[] = [
      ...mappedRows,
      {
        id: totalRowId,
        client: 'Total',
        value_start: totalValueStart,
        balance: totalBalance,
        days_late: '-',
        date: undefined,
        date_refinance: '-',
        time_refinance: '-',
        total_value: totalRefinanced,
        quote: '-',
      },
    ];

    const createCurrencyCell = (field: keyof RefinanceRow) => ({ row }: { row: SimpleRow }) => {
      const typedRow = row as RefinanceRow;
      const value = Number(typedRow[field] ?? 0);
      return (
        <Typography
          variant="body2"
          sx={typedRow.id === totalRowId ? totalCellStyles : undefined}
        >
          {formatCurrency(value)}
        </Typography>
      );
    };

    const renderClientCell = ({ row }: { row: SimpleRow }) => {
      const typedRow = row as RefinanceRow;
      if (typedRow.id === totalRowId) {
        return (
          <Typography variant="body2" sx={totalCellStyles}>
            {typedRow.client}
          </Typography>
        );
      }

      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleOpenModalLendType(typedRow)}
            sx={{ minWidth: 25, padding: '2px', minHeight: 'unset', lineHeight: 1 }}
          >
            <SearchIcon fontSize="small" />
          </Button>
          <Typography variant="body2">{typedRow.client}</Typography>
        </Box>
      );
    };

    const renderQuoteCell = ({ row }: { row: SimpleRow }) => {
      const typedRow = row as RefinanceRow;
      const content = typeof typedRow.quote === 'number' ? typedRow.quote : (typedRow.quote ?? '-');
      return (
        <Typography
          variant="body2"
          sx={typedRow.id === totalRowId ? totalCellStyles : undefined}
        >
          {content}
        </Typography>
      );
    };

    const columns: SimpleColumn[] = [
      {
        field: 'client',
        headerName: 'Cliente',
        minWidth: isMobile ? 260 : 540,
        wrapText: true,
        renderCell: renderClientCell,
      },
      {
        field: 'value_start',
        headerName: 'Valor Préstamo',
        align: 'right',
        minWidth: 160,
        renderCell: createCurrencyCell('value_start'),
      },
      {
        field: 'balance',
        headerName: 'Saldo',
        align: 'right',
        minWidth: 160,
        renderCell: createCurrencyCell('balance'),
      },
      {
        field: 'days_late',
        headerName: 'Días Atraso',
        align: 'center',
        minWidth: 140,
        renderCell: ({ row }: { row: SimpleRow }) => {
          const typedRow = row as RefinanceRow;
          return (
            <Typography
              variant="body2"
              sx={typedRow.id === totalRowId ? totalCellStyles : undefined}
            >
              {typedRow.id === totalRowId ? '-' : typedRow.days_late ?? '-'}
            </Typography>
          );
        },
      },
      {
        field: 'date_refinance',
        headerName: 'Fecha',
        minWidth: 140,
      },
      {
        field: 'time_refinance',
        headerName: 'Hora',
        minWidth: 140,
      },
      {
        field: 'total_value',
        headerName: 'Valor Refinanciado',
        align: 'right',
        minWidth: 170,
        renderCell: createCurrencyCell('total_value'),
      },
      {
        field: 'quote',
        headerName: 'Cuotas',
        align: 'center',
        minWidth: 120,
        renderCell: renderQuoteCell,
      },
    ];

    return (
      <SimpleCustomTable
        rows={rows}
        columns={columns}
        maxHeight={600}
        showBorders
        showCount
        exportCsv
        exportFileName="refinanciacion.csv"
        wrapCellContent
      />
    );
  };

  return (
    <HeaderResponsive>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Reporte de Refinanciacion</Title>
        <Button variant="contained" onClick={submit} startIcon={!isMobile && <SearchIcon />}>
          {isMobile ? <SearchIcon /> : 'Buscar'}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <CollectFilter
            start_date={start_date}
            end_date={end_date}
            office_id={office_id}
            setStart_date={setStart_date}
            setEnd_date={setEnd_date}
            setOffice_id={setOffice_id}
            offices={offices}
            collectsOffice={collectsOffice}
            selectedCollects={selectedCollects}
            setSelectedCollects={setSelectedCollects}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            handleSelectAll={handleSelectAll}
          />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ maxHeight: 800, overflowY: 'auto' }}>
            {datas.length > 0 ? (
              datas.map((grupo: any) => (
                <Box key={grupo.collect_id} display="flex" flexDirection="column" minWidth={0}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Préstramos Refinanciados - {grupo.collect_name}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Fechas:</strong> {start_date} al {end_date}
                      </Typography>
                    </Grid>
                  </Grid>
                  {renderDataGridPorCobro(grupo.data)}
                  <Divider sx={{ my: 4, borderColor: 'black' }} />
                </Box>
              ))
            ) : (
              <EmptyStateMessage message="No existen registros" align="left" />
            )}
            <Divider sx={{ my: 4 }} />
          </Box>
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default RefinanceLend;
